import React, { FC, useCallback } from 'react';
import translate from '@translate';
import BodyTopBlock, { ActionType } from 'src/components/BodyTopBlock';
import { ExportTypeType, SelectRowType } from 'src/types/table';
import ExportTableAction from 'src/components/ExportTableAction';
import StoreIcon from '@mui/icons-material/Store';
import ArchiveIcon from '@mui/icons-material/Archive';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import FlipIcon from '@mui/icons-material/Flip';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AssignmentReturnedIcon from '@mui/icons-material/AssignmentReturned';
import { AddIcon } from 'src/components/SkladonIcon/Icons';
import { useNavigate } from 'react-router-dom';
import { ConstantsCodesObjectType, ConstantsCodeType } from '../../types/constantsCodes';
import { checkPermission, useCheckPermission } from '../../utils/checkPermission';
import filterUnique from '../../utils/filterUnique';
import { getAllowedStatuses } from '../ReturnDetailContainer/functions';
import { ReturnType } from '../../types/return';

type TopBlockProps = {
    handleExport: (type: ExportTypeType, withProducts?: boolean) => void;
    allProviders: boolean;
    archive: boolean;
    permissionArchive: boolean;
    setArchive: (value: boolean) => void;
    setAllProviders: React.Dispatch<React.SetStateAction<boolean>>;
    reloadTableData: () => void;
    selectedRows: SelectRowType[];
    permissionReturnCreate: boolean;
    permissionSynchronizeDcix: boolean;
    handleSynchronizeDCIX: () => void;
    permissionAllProviders: boolean;
    returnsDataData?: ReturnType[];
    returnStatusCodes: ConstantsCodeType[];
    returnStatusCodesObject: ConstantsCodesObjectType;
    handleMassChangeStatus: (statuses: ConstantsCodeType[]) => void;
    permissionReturnUpdate: boolean;
};

const TopBlock: FC<TopBlockProps> = (props) => {
    const getEnableMultipleActions = (): {
        ableEditStatuses: boolean;
        allowedStatuses: ConstantsCodeType[];
    } | null => {
        if (!props.selectedRows.length || !props.returnsDataData) {
            return null;
        }

        let ableEditStatuses = props.permissionReturnUpdate;

        const selectedStatuses: string[] = [];

        props.returnsDataData
            .filter((item) => props.selectedRows.includes(String(item.ID)))
            .forEach((item) => {
                if (item.STATUS) {
                    selectedStatuses.push(item.STATUS);
                }
            });

        let allowedStatuses: ConstantsCodeType[] = [];
        if (ableEditStatuses) {
            selectedStatuses.filter(filterUnique).forEach((status, index) => {
                if (index === 0) {
                    allowedStatuses = getAllowedStatuses(
                        props.returnStatusCodes,
                        props.returnStatusCodesObject,
                        { STATUS: status },
                    );
                } else {
                    const thisStatusStatuses = getAllowedStatuses(
                        props.returnStatusCodes,
                        props.returnStatusCodesObject,
                        { STATUS: status },
                    ).map((status) => status.value);
                    allowedStatuses = allowedStatuses.filter((status) => thisStatusStatuses.includes(status.value));
                }
            });
        }
        return {
            ableEditStatuses,
            allowedStatuses
        };
    };

    const ableMultipleActions = getEnableMultipleActions();

    const navigate = useNavigate();
    const permissionExportReturns = useCheckPermission('RETURN.EXPORT.TABLE');
    const permissionExportReturnsProducts = useCheckPermission('RETURN.EXPORT.PRODUCTS');

    const handleNew = useCallback(() => {
        navigate(`/returns/new/`);
    }, []);

    const actions: ActionType[] = [
        {
            node: (
                <ExportTableAction
                    title={translate('returns.action.exportFile')}
                    handleExport={(type) => props.handleExport(type)}
                />
            ),
            hidden: props.archive || !permissionExportReturns,
        },
        {
            onClick: () => props.setAllProviders(!props.allProviders),
            icon: <StoreIcon />,
            tooltip: translate('returns.allProviders'),
            variant: 'outlined',
            active: props.allProviders,
            hidden: !props.permissionAllProviders,
            disabled: props.archive,
        },
        {
            onClick: () => props.setArchive(!props.archive),
            icon: <ArchiveIcon />,
            label: translate('archive.toggle'),
            tooltip: translate('archive.tooltip'),
            variant: 'outlined',
            active: props.archive,
            hidden: !props.permissionArchive,
            'data-testid': 'archive-toggle',
        },
        {
            onClick: () => props.reloadTableData(),
            icon: <AutorenewIcon />,
            tooltip: translate('returns.action.synchronizeTable'),
            variant: 'outlined',
        },
        {
            hidden: props.archive,
            items: [
                {
                    onClick: () => props.handleSynchronizeDCIX(),
                    icon: <SyncAltIcon />,
                    label: translate('returns.action.synchronizeDcix'),
                    variant: 'text',
                    hidden: !props.selectedRows.length || !props.permissionSynchronizeDcix,
                },
                { divider: true },

                {
                    node: (
                        <ExportTableAction
                            title={translate('returns.action.exportWithProducts')}
                            handleExport={(type) => props.handleExport(type, true)}
                            icon={<AssignmentReturnedIcon />}
                            textWithIcon
                        />
                    ),
                    hidden: props.archive || props.allProviders || !permissionExportReturnsProducts,
                },
                {
                    onClick: () =>
                        ableMultipleActions?.allowedStatuses &&
                        props.handleMassChangeStatus(ableMultipleActions?.allowedStatuses),
                    icon: <FlipIcon />,
                    label: translate('returns.action.changeStatuses'),
                    variant: 'text',
                    hidden: !ableMultipleActions?.allowedStatuses.length
                },
            ],
        },
        {
            label: translate('orders.action.createNew'),
            onClick: handleNew,
            icon: <AddIcon />,
            hidden: props.archive || props.allProviders || !props.permissionReturnCreate,
        },
    ];

    return <BodyTopBlock actions={actions} />;
};

export default TopBlock;
